import { slugify } from '@/lib/utils/slugify';
import { stegaClean } from '@/lib/utils/stega';
import { VEHICLE_BASE_PATH } from './vehicle.constants';

const ID_PREFIX = 'vehicle-';

/** Hex characters of the id the URL carries — 48 bits, unique far beyond any stock. */
const KEY_LENGTH = 12;

const KEY_PATTERN = new RegExp(`(?:^|-)([0-9a-f]{${KEY_LENGTH}})$`);

type VehicleRef = { _id: string; title?: string };

/** The id's hash part: the import derives `vehicle-<hash>` from the FIN. */
function keyOf(id: string): string {
    return stegaClean(id).slice(ID_PREFIX.length, ID_PREFIX.length + KEY_LENGTH);
}

/**
 * „Marke Modell Linie“ plus the key. The detail page looks the vehicle up by the
 * key alone, so a changed title still resolves and redirects to the new slug.
 */
export function vehicleSlug(vehicle: VehicleRef): string {
    const name = slugify(stegaClean(vehicle.title ?? ''));
    const key = keyOf(vehicle._id);

    return name ? `${name}-${key}` : key;
}

export function vehicleHref(vehicle: VehicleRef): string {
    return `${VEHICLE_BASE_PATH}/${vehicleSlug(vehicle)}`;
}

/** The key alone — an inquiry link hands it to the form page (`VEHICLE_KEY_PARAM`). */
export function vehicleKey(vehicle: VehicleRef): string {
    return keyOf(vehicle._id);
}

/** The id prefix a key (or a slug ending in one) points to, or null when it cannot be a vehicle. */
export function vehicleIdPrefixFromKey(key: string): string | null {
    const match = key.match(KEY_PATTERN);
    return match ? `${ID_PREFIX}${match[1]}` : null;
}

/** The id prefix a detail URL points to, or null when the segment cannot be a vehicle. */
export function vehicleIdPrefix(slug: string): string | null {
    return vehicleIdPrefixFromKey(decodeURIComponent(slug));
}
